import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    Grid,
    IconButton,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { Save as SaveIcon, Refresh as RefreshIcon, Link as LinkIcon, LinkOff as LinkOffIcon, Send as SendIcon } from '@mui/icons-material';
import { AdminPageContainer, StaticCard } from '../../../components/admin/sharedStyles';
import PageHeader from '../../../components/PageHeader';
import ConfigSubMenu from '../../../components/admin/ConfigSubMenu';
import { useDispatch } from 'react-redux';
import { showAlert } from '../../../redux/slices/alerts';
import { getConfigValue, saveConfig, getWhatsAppStatus, getWhatsAppQr, startWhatsAppSession, logoutWhatsAppSession, testWhatsAppMessage } from '../../../redux/slices/config';
import useUnsavedChanges from '../../../hooks/useUnsavedChanges';
import ConfirmDialog from '../../../components/admin/ConfirmDialog';

const STATUS_POLL_MS = 3000;
const CONNECTED_POLL_MS = 15000;
const STATUS_RETRY_POLL_MS = 15000;
const QR_REFRESH_MS = 10000;

const EMPTY_SETTINGS = { enabled: false, baseUrl: '', session: '', defaultCountryCode: '' };

const STATUS_META = {
    NOT_CREATED: { label: 'Not set up', color: 'default', detail: 'No WhatsApp session exists on the gateway yet. Select Connect to create one and get a QR code.' },
    STOPPED: { label: 'Disconnected', color: 'default', detail: 'The session exists but is not running. Select Connect to start it.' },
    STARTING: { label: 'Starting…', color: 'info', detail: 'The gateway is starting the session. This usually takes a few seconds.' },
    SCAN_QR_CODE: { label: 'Waiting for scan', color: 'warning', detail: 'Scan the code below with the phone that owns the WhatsApp Business account.' },
    WORKING: { label: 'Connected', color: 'success', detail: 'The gateway is paired and able to send messages.' },
    FAILED: { label: 'Failed', color: 'error', detail: 'The gateway could not keep the session running. Select Connect to restart it and pair again.' },
};

const UNKNOWN_STATUS_META = { label: 'Unknown', color: 'default', detail: 'The gateway reported a state this page does not recognise.' };

// The status endpoint answers 200 with an error string when the gateway is down, so these are
// the shapes the raw text arrives in and must never be shown to an admin as-is.
const TECHNICAL_ERROR_PATTERNS = [
    /ECONNREFUSED/i,
    /ECONNRESET/i,
    /ENOTFOUND/i,
    /EAI_AGAIN/i,
    /ETIMEDOUT/i,
    /EHOSTUNREACH/i,
    /ENETUNREACH/i,
    /getaddrinfo/i,
    /socket hang up/i,
    /timeout of \d+ms/i,
    /Network Error/i,
    /Request failed with status/i,
];

const UNREACHABLE_SUMMARY = 'Pollux could not reach the WhatsApp gateway.';

const describeGatewayError = (raw) => {
    const text = String(raw ?? '').trim();
    if (!text) return { summary: UNREACHABLE_SUMMARY, detail: '' };
    if (/api[ _-]?key/i.test(text)) return { summary: 'The WhatsApp gateway key is not set on the Pollux server.', detail: text };
    if (TECHNICAL_ERROR_PATTERNS.some((pattern) => pattern.test(text))) return { summary: UNREACHABLE_SUMMARY, detail: text };
    return { summary: text, detail: '' };
};

const PAIRING_STEPS = [
    'Open WhatsApp on the phone that owns the business number.',
    'Go to Settings, then Linked devices.',
    'Tap Link a device and unlock the phone if asked.',
    'Point the phone camera at the code on this screen.',
];

const countryCodeError = (value) => {
    const raw = String(value ?? '').trim();
    if (!raw) return 'Enter the country code used when a saved number has no country code of its own';
    if (!/^\d{1,3}$/.test(raw)) return 'Use 1 to 3 digits only — no plus sign, spaces or letters';
    return '';
};

const baseUrlError = (value) => {
    const raw = String(value ?? '').trim();
    if (!raw) return 'Enter the address of the WhatsApp gateway, for example http://waha:3000';
    if (!/^https?:\/\/\S+$/i.test(raw)) return 'Start the address with http:// or https://';
    return '';
};

const sessionError = (value) => (String(value ?? '').trim() ? '' : 'Enter the session name the gateway stores this pairing under, for example default');

const describeAccount = (me) => {
    if (!me || typeof me !== 'object') return '';
    const number = String(me.id || me.phone || '').split('@')[0];
    const name = me.pushName || me.name || '';
    if (name && number) return `${name} (+${number})`;
    if (number) return `+${number}`;
    return name;
};

const LoadingRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
}));

const SectionCard = styled(StaticCard)(({ theme }) => ({
    marginBottom: theme.spacing(3),
}));

const SectionSubtitle = styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const SwitchNote = styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const ActionsRow = styled(Box)({
    display: 'flex',
    justifyContent: 'flex-end',
});

const StatusRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
}));

const StatusDetail = styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const AccountLine = styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    fontWeight: theme.typography.fontWeightMedium,
}));

const InlineAlert = styled(Alert)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const PairingBlock = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: theme.spacing(3),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.action.hover,
}));

const QrFrame = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 320,
    height: 320,
    padding: theme.spacing(2),
    backgroundColor: '#ffffff',
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
}));

const QrFrameMessage = styled(Typography)(({ theme }) => ({
    maxWidth: 260,
    textAlign: 'center',
    color: theme.palette.text.secondary,
}));

const TechnicalDetail = styled(Typography)(({ theme }) => ({
    display: 'block',
    marginTop: theme.spacing(1),
    color: theme.palette.text.disabled,
    wordBreak: 'break-word',
}));

const QrImage = styled('img')({
    width: 288,
    height: 288,
    display: 'block',
    imageRendering: 'pixelated',
});

const PairingSteps = styled('ol')(({ theme }) => ({
    margin: 0,
    paddingLeft: theme.spacing(2.5),
    maxWidth: 360,
    color: theme.palette.text.secondary,
}));

const PairingStep = styled('li')(({ theme }) => ({
    marginBottom: theme.spacing(1),
}));

const PairingHeading = styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(1),
}));

const ConnectionActions = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(1),
}));

const WhatsAppSettings = () => {
    const dispatch = useDispatch();
    const isMountedRef = useRef(true);
    const statusInFlightRef = useRef(false);

    const [loading, setLoading] = useState(true);
    const [settingsLoadError, setSettingsLoadError] = useState('');
    const [saving, setSaving] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [settings, setSettings] = useState(EMPTY_SETTINGS);

    const [statusLoading, setStatusLoading] = useState(true);
    const [connection, setConnection] = useState(null);
    const [statusError, setStatusError] = useState('');

    const [qr, setQr] = useState(null);
    const [qrError, setQrError] = useState('');

    const [starting, setStarting] = useState(false);
    const [disconnecting, setDisconnecting] = useState(false);
    const [testing, setTesting] = useState(false);
    const [confirmDisconnect, setConfirmDisconnect] = useState(false);
    const [confirmRepoint, setConfirmRepoint] = useState(false);
    const storedRef = useRef(EMPTY_SETTINGS);
    const [testDialogOpen, setTestDialogOpen] = useState(false);
    const [testPhone, setTestPhone] = useState('');

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const fetchStatus = useCallback(
        (silent) => {
            // Against a hung gateway each poll takes the full request timeout, so without this the
            // 3s interval stacks up requests for as long as the page stays open.
            if (statusInFlightRef.current) return;
            statusInFlightRef.current = true;
            dispatch(
                getWhatsAppStatus({
                    silent,
                    callback: (data, message) => {
                        statusInFlightRef.current = false;
                        if (!isMountedRef.current) return;
                        setStatusLoading(false);
                        if (!data) {
                            setConnection(null);
                            setStatusError(message || 'Could not reach the WhatsApp gateway status endpoint.');
                            return;
                        }
                        setStatusError('');
                        setConnection(data);
                    },
                }),
            );
        },
        [dispatch],
    );

    const fetchQr = useCallback(() => {
        dispatch(
            getWhatsAppQr({
                silent: true,
                callback: (data, message) => {
                    if (!isMountedRef.current) return;
                    if (!data?.data) {
                        setQr(null);
                        setQrError(message || 'The gateway has no pairing code to show right now.');
                        return;
                    }
                    setQrError('');
                    setQr(data);
                },
            }),
        );
    }, [dispatch]);

    useEffect(() => {
        setLoading(true);
        dispatch(
            getConfigValue({
                key: 'whatsappConfig',
                value: null,
                silent: true,
                callback: (data) => {
                    if (!isMountedRef.current) return;
                    const val = data?.value;
                    if (val && typeof val === 'object') {
                        setSettingsLoadError('');
                        const loaded = {
                            enabled: !!val.enabled,
                            baseUrl: val.baseUrl != null ? String(val.baseUrl) : '',
                            session: val.session != null ? String(val.session) : '',
                            defaultCountryCode: val.defaultCountryCode != null ? String(val.defaultCountryCode) : '',
                        };
                        storedRef.current = loaded;
                        setSettings(loaded);
                    } else {
                        setSettingsLoadError(
                            'The saved WhatsApp settings could not be loaded, so the fields below are empty. Reload the page before saving, or you will overwrite the stored settings with blanks.',
                        );
                    }
                    setLoading(false);
                },
            }),
        );
        fetchStatus(false);
    }, [dispatch, fetchStatus]);

    const connectionState = connection?.status || '';
    const configured = connection?.configured === true;
    const isConnected = connectionState === 'WORKING';
    const isScanning = connectionState === 'SCAN_QR_CODE';
    // A gateway that is down answers 200 with an error string, so statusError alone never sees it.
    const connectionError = connection?.error || '';

    useEffect(() => {
        const delay = statusError || connectionError ? STATUS_RETRY_POLL_MS : isConnected ? CONNECTED_POLL_MS : STATUS_POLL_MS;
        const timer = setInterval(() => fetchStatus(true), delay);
        return () => clearInterval(timer);
    }, [isConnected, statusError, connectionError, fetchStatus]);

    useEffect(() => {
        if (!isScanning) {
            setQr(null);
            setQrError('');
            return undefined;
        }
        fetchQr();
        const timer = setInterval(fetchQr, QR_REFRESH_MS);
        return () => clearInterval(timer);
    }, [isScanning, fetchQr]);

    const { showPrompt, confirmLeave, cancelLeave, message: unsavedMessage } = useUnsavedChanges(isDirty);

    const setField = (field, value) => {
        setIsDirty(true);
        setSettings((prev) => ({ ...prev, [field]: value }));
    };

    const fieldErrors = useMemo(
        () => ({
            baseUrl: baseUrlError(settings.baseUrl),
            session: sessionError(settings.session),
            defaultCountryCode: countryCodeError(settings.defaultCountryCode),
        }),
        [settings],
    );

    const hasFieldErrors = Object.values(fieldErrors).some(Boolean);

    const unavailable = useMemo(() => {
        if (connection && !configured) {
            const { detail } = describeGatewayError(connection.error);
            return {
                label: 'Not available',
                color: 'warning',
                severity: 'warning',
                title: 'The WhatsApp gateway key is missing on the server',
                summary:
                    'Pollux cannot talk to the WhatsApp gateway until the gateway key is set in the server environment. Pairing cannot proceed and the buttons below stay disabled until it is present.',
                detail,
            };
        }
        if (statusError || connectionError) {
            const { summary, detail } = describeGatewayError(statusError || connectionError);
            return {
                label: 'Not available',
                color: 'error',
                severity: 'error',
                title: 'Pollux cannot reach the WhatsApp gateway',
                summary: `${summary} Pollux will keep retrying in the background, or use the refresh button above to try again now.`,
                detail,
            };
        }
        return null;
    }, [connection, configured, statusError, connectionError]);

    const qrMessage = useMemo(() => (qrError ? describeGatewayError(qrError) : null), [qrError]);

    const statusMeta = connectionState ? STATUS_META[connectionState] || UNKNOWN_STATUS_META : UNKNOWN_STATUS_META;
    const accountLabel = describeAccount(connection?.me);
    const actionsBlocked = !connection || !configured || !!connectionError || starting || disconnecting;

    const persistSettings = (onSaved) => {
        const value = {
            enabled: settings.enabled,
            baseUrl: settings.baseUrl.trim(),
            session: settings.session.trim(),
            defaultCountryCode: settings.defaultCountryCode.trim(),
        };
        setSaving(true);
        dispatch(
            saveConfig({
                key: 'whatsappConfig',
                value,
                callback: (data) => {
                    if (!isMountedRef.current) return;
                    setSaving(false);
                    const saved = !!data && data.status !== 0;
                    if (saved) {
                        storedRef.current = value;
                        setIsDirty(false);
                    }
                    if (onSaved) {
                        onSaved(saved);
                        return;
                    }
                    if (!saved) return;
                    dispatch(showAlert({ severity: 'success', message: 'WhatsApp settings saved successfully' }));
                    fetchStatus(true);
                },
            }),
        );
    };

    const repointsAwayFromPairing = isConnected && (settings.session.trim() !== storedRef.current.session || settings.baseUrl.trim() !== storedRef.current.baseUrl);

    const repointMessage = () => {
        const from = storedRef.current.session;
        const target = settings.session.trim() !== from ? `"${settings.session.trim()}"` : `"${from}" on ${settings.baseUrl.trim()}`;
        return `Pollux is paired to "${from}" and can send messages from it now. Saving points Pollux at ${target} instead, which has to be paired with a QR code first — the "${from}" pairing stays on the gateway, so putting the old details back here returns to it.`;
    };

    const handleSave = () => {
        if (hasFieldErrors) {
            dispatch(
                showAlert({ severity: 'error', message: 'Fix the highlighted fields before saving. The gateway address, session name and default country code are all required.' }),
            );
            return;
        }
        if (repointsAwayFromPairing) {
            setConfirmRepoint(true);
            return;
        }
        persistSettings();
    };

    const startSession = () => {
        setStarting(true);
        dispatch(
            startWhatsAppSession({
                callback: (data) => {
                    if (!isMountedRef.current) return;
                    setStarting(false);
                    if (!data) return;
                    dispatch(showAlert({ severity: 'info', message: 'Starting the WhatsApp session. The pairing code appears here as soon as the gateway is ready.' }));
                    fetchStatus(true);
                },
            }),
        );
    };

    const handleConnect = () => {
        if (settings.session.trim() === storedRef.current.session && settings.baseUrl.trim() === storedRef.current.baseUrl) {
            startSession();
            return;
        }
        if (hasFieldErrors) {
            dispatch(
                showAlert({ severity: 'error', message: 'Fix the highlighted fields before connecting. The gateway address, session name and default country code are all required.' }),
            );
            return;
        }
        persistSettings((saved) => {
            if (saved) startSession();
        });
    };

    const handleDisconnect = () => {
        setConfirmDisconnect(false);
        setDisconnecting(true);
        dispatch(
            logoutWhatsAppSession({
                callback: (data) => {
                    if (!isMountedRef.current) return;
                    setDisconnecting(false);
                    if (!data) return;
                    dispatch(showAlert({ severity: 'success', message: 'The WhatsApp account has been unlinked. Select Connect to pair again.' }));
                    fetchStatus(true);
                },
            }),
        );
    };

    const handleSendTest = () => {
        setTesting(true);
        dispatch(
            testWhatsAppMessage({
                phone: testPhone.trim() || undefined,
                callback: (data) => {
                    if (!isMountedRef.current) return;
                    setTesting(false);
                    if (!data) return;
                    setTestDialogOpen(false);
                    dispatch(
                        showAlert({
                            severity: data.sent ? 'success' : 'warning',
                            message:
                                data.message ||
                                (data.sent ? `Test message sent to ${data.chatId || 'the saved number'}` : 'The gateway did not confirm the test message was sent.'),
                        }),
                    );
                },
            }),
        );
    };

    return (
        <AdminPageContainer>
            <PageHeader title="WhatsApp" subtitle="Pair a WhatsApp Business account with the self-hosted gateway so Pollux can send messages from it." />

            <ConfigSubMenu />

            <SectionCard>
                <StatusRow>
                    <Typography variant="h6">Connection</Typography>
                    {statusLoading ? (
                        <CircularProgress size={20} />
                    ) : (
                        <Chip label={unavailable ? unavailable.label : statusMeta.label} color={unavailable ? unavailable.color : statusMeta.color} />
                    )}
                    <Tooltip title="Check the gateway status now">
                        <span>
                            <IconButton size="small" aria-label="Check the gateway status now" onClick={() => fetchStatus(false)}>
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </StatusRow>

                {unavailable ? (
                    <InlineAlert severity={unavailable.severity}>
                        <AlertTitle>{unavailable.title}</AlertTitle>
                        {unavailable.summary}
                        {unavailable.detail && <TechnicalDetail variant="caption">Details for your administrator: {unavailable.detail}</TechnicalDetail>}
                    </InlineAlert>
                ) : (
                    <StatusDetail variant="body2" color="text.secondary">
                        {statusLoading ? 'Checking the gateway…' : statusMeta.detail}
                    </StatusDetail>
                )}

                {isConnected && accountLabel && <AccountLine variant="body1">Connected account: {accountLabel}</AccountLine>}

                {isScanning && (
                    <PairingBlock>
                        <QrFrame>
                            {qr?.data ? (
                                <QrImage src={`data:${qr.mimetype || 'image/png'};base64,${qr.data}`} alt="WhatsApp pairing QR code" />
                            ) : qrMessage ? (
                                <QrFrameMessage variant="body2">
                                    {qrMessage.summary}
                                    {qrMessage.detail && <TechnicalDetail variant="caption">Details for your administrator: {qrMessage.detail}</TechnicalDetail>}
                                </QrFrameMessage>
                            ) : (
                                <CircularProgress />
                            )}
                        </QrFrame>
                        <Box>
                            <PairingHeading variant="subtitle1">How to pair</PairingHeading>
                            <PairingSteps>
                                {PAIRING_STEPS.map((step) => (
                                    <PairingStep key={step}>
                                        <Typography variant="body2" color="text.secondary">
                                            {step}
                                        </Typography>
                                    </PairingStep>
                                ))}
                            </PairingSteps>
                            <StatusDetail variant="caption" color="text.secondary" component="p">
                                A new code replaces this one every 10 seconds. If the phone says the code has expired, just wait for the next one.
                            </StatusDetail>
                        </Box>
                    </PairingBlock>
                )}

                <ConnectionActions>
                    {!isConnected && (
                        <Button variant="contained" color="primary" startIcon={<LinkIcon />} onClick={handleConnect} disabled={actionsBlocked}>
                            {starting ? 'Connecting...' : 'Connect'}
                        </Button>
                    )}
                    {isConnected && (
                        <Button variant="outlined" color="error" startIcon={<LinkOffIcon />} onClick={() => setConfirmDisconnect(true)} disabled={actionsBlocked}>
                            {disconnecting ? 'Disconnecting...' : 'Disconnect'}
                        </Button>
                    )}
                    <Button variant="outlined" startIcon={<SendIcon />} onClick={() => setTestDialogOpen(true)} disabled={actionsBlocked || !isConnected}>
                        Send test message
                    </Button>
                </ConnectionActions>
            </SectionCard>

            {loading ? (
                <LoadingRow>
                    <CircularProgress />
                </LoadingRow>
            ) : (
                <>
                    <SectionCard>
                        <Typography variant="h6" gutterBottom>
                            Settings
                        </Typography>
                        <SectionSubtitle variant="body2" color="text.secondary">
                            Where the gateway lives and how Pollux talks to it. Changing the address or session name here does not move an existing pairing — the new session has to
                            be paired again.
                        </SectionSubtitle>

                        {settingsLoadError && <InlineAlert severity="error">{settingsLoadError}</InlineAlert>}

                        <FormControlLabel control={<Switch checked={settings.enabled} onChange={(e) => setField('enabled', e.target.checked)} />} label="Send WhatsApp messages" />
                        <SwitchNote variant="body2" color="text.secondary">
                            Master switch. When off, nothing is sent over WhatsApp even if the account below is paired and working.
                        </SwitchNote>

                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Gateway address"
                                    value={settings.baseUrl}
                                    onChange={(e) => setField('baseUrl', e.target.value)}
                                    error={!!fieldErrors.baseUrl}
                                    helperText={fieldErrors.baseUrl || 'The address of the self-hosted WhatsApp gateway, reachable from the Pollux server'}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Session name"
                                    value={settings.session}
                                    onChange={(e) => setField('session', e.target.value)}
                                    error={!!fieldErrors.session}
                                    helperText={fieldErrors.session || 'The name the gateway files this pairing under. One paired phone per session name.'}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Default country code"
                                    value={settings.defaultCountryCode}
                                    onChange={(e) => setField('defaultCountryCode', e.target.value)}
                                    error={!!fieldErrors.defaultCountryCode}
                                    helperText={
                                        fieldErrors.defaultCountryCode ||
                                        'Digits only, no plus sign. Any saved phone number that does not already start with a plus gets this added in front of it, so the wrong value here sends messages to the wrong country.'
                                    }
                                />
                            </Grid>
                        </Grid>
                    </SectionCard>

                    <ActionsRow>
                        <Button variant="contained" color="primary" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}>
                            {saving ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </ActionsRow>
                </>
            )}

            <ConfirmDialog
                open={confirmDisconnect}
                onClose={() => setConfirmDisconnect(false)}
                onConfirm={handleDisconnect}
                title="Disconnect this WhatsApp account?"
                message="Pollux will unlink the phone and stop sending WhatsApp messages straight away. To use WhatsApp again someone has to scan a new QR code with that same phone, so only do this if the phone is available."
                confirmLabel="Disconnect"
                confirmColor="error"
            />

            <ConfirmDialog
                open={confirmRepoint}
                onClose={() => setConfirmRepoint(false)}
                onConfirm={() => {
                    setConfirmRepoint(false);
                    persistSettings();
                }}
                title="Point Pollux at a different WhatsApp session?"
                message={repointMessage()}
                confirmLabel="Change session"
                confirmColor="warning"
            />

            <Dialog open={testDialogOpen} onClose={() => setTestDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Send a test WhatsApp message</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This sends one short message through the paired account so you can confirm it arrives. Leave the box empty to send it to the mobile number on your own Pollux
                        profile.
                    </DialogContentText>
                    <TextField
                        fullWidth
                        margin="normal"
                        label="Phone number (optional)"
                        value={testPhone}
                        onChange={(e) => setTestPhone(e.target.value)}
                        helperText="Include the country code, or the default country code from the settings below is added for you."
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTestDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="primary" startIcon={<SendIcon />} onClick={handleSendTest} disabled={testing}>
                        {testing ? 'Sending...' : 'Send test message'}
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={showPrompt}
                onClose={cancelLeave}
                onConfirm={confirmLeave}
                title="Unsaved Changes"
                message={unsavedMessage}
                confirmLabel="Leave"
                confirmColor="warning"
            />
        </AdminPageContainer>
    );
};

export default WhatsAppSettings;
